/**
 * Pure utility functions for condition DSL manipulation
 * Separated from ConditionBuilder for testability
 */

// DSL Types - Keep compatible with backend
export interface ConditionNode {
  all?: ConditionNode[];
  any?: ConditionNode[];
  not?: ConditionNode;
  compare?: CompareCondition;
  timeBetween?: TimeBetweenCondition;
}

export interface CompareCondition {
  left: Operand;
  op: 'eq' | 'ne' | 'gt' | 'gte' | 'lt' | 'lte';
  right: Operand;
  hysteresis?: {
    release: number;
  };
}

export interface TimeBetweenCondition {
  start: string; // HH:MM:SS
  end: string; // HH:MM:SS
}

export type Operand =
  | { sensor: string }
  | { literal: number }
  | { add: [Operand, Operand] }
  | { sub: [Operand, Operand] };

export interface SensorInfo {
  id: string;
  humanId: string;
  name: string;
  type: 'number' | 'boolean' | 'time';
  group: 'Synthetic' | 'Device' | 'Other';
  value?: number;
  unitSymbol?: string;
}

const SENSOR_TIME_OF_DAY = 'synthetic.time.local-second-of-day';
const SENSOR_SUNRISE = 'synthetic.sun.sunrise-second-of-day';
const SENSOR_SUNSET = 'synthetic.sun.sunset-second-of-day';

export type DayNightCondition = {
  type: 'daytime' | 'nighttime';
  startOffsetSeconds: number;
  endOffsetSeconds: number;
};

/**
 * Get the DSL sensor ID (prefer humanId when available)
 */
export function getSensorDslId(sensor: { humanId?: string; id?: string }): string {
  if ('humanId' in sensor && sensor.humanId) {
    return sensor.humanId;
  }
  return sensor.id ?? '';
}

/**
 * Check if a group represents a day-of-week condition
 */
export function isDayOfWeekGroup(node: ConditionNode): boolean {
  const children = node.any ?? node.all;
  if (!children || children.length === 0) return false;

  return children.every((child) => {
    if (!child.compare) return false;
    const leftSensor = (child.compare.left as { sensor?: string }).sensor;
    return leftSensor === 'synthetic.time.iso-weekday';
  });
}

/**
 * Extract selected days from a day-of-week group
 */
export function extractSelectedDays(node: ConditionNode): number[] {
  const children = node.any ?? [];
  return children
    .filter((child) => child.compare)
    .map((child) => {
      const right = child.compare!.right as { literal?: number };
      return right.literal ?? 0;
    })
    .filter((day) => day >= 1 && day <= 7);
}

/**
 * Build a day-of-week condition from selected days
 * Uses weekend shortcut when only Saturday and Sunday are selected
 */
export function buildDayOfWeekCondition(days: number[]): ConditionNode {
  // Special case: Weekend preset
  if (days.length === 2 && days.includes(6) && days.includes(7)) {
    return {
      compare: {
        left: { sensor: 'synthetic.time.is-weekend' },
        op: 'eq',
        right: { literal: 1 },
      },
    };
  }

  // Build OR group of eq comparisons
  return {
    any: days.map((day) => ({
      compare: {
        left: { sensor: 'synthetic.time.iso-weekday' },
        op: 'eq' as const,
        right: { literal: day },
      },
    })),
  };
}

function isSensorOperand(operand: Operand, sensorId: string): boolean {
  return 'sensor' in operand && operand.sensor === sensorId;
}

function getLiteralValue(operand: Operand): number | null {
  if ('literal' in operand && typeof operand.literal === 'number') {
    return operand.literal;
  }
  return null;
}

function parseOffsetOperand(operand: Operand, sensorId: string): number | null {
  if (isSensorOperand(operand, sensorId)) {
    return 0;
  }
  if ('add' in operand) {
    const [base, offset] = operand.add;
    if (!isSensorOperand(base, sensorId)) return null;
    const value = getLiteralValue(offset);
    return value === null ? null : value;
  }
  if ('sub' in operand) {
    const [base, offset] = operand.sub;
    if (!isSensorOperand(base, sensorId)) return null;
    const value = getLiteralValue(offset);
    return value === null ? null : -value;
  }
  return null;
}

function parseSunCompare(
  node: ConditionNode,
  op: CompareCondition['op'],
  sensorId: string
): number | null {
  if (!node.compare) return null;
  const left = node.compare.left as { sensor?: string };
  if (left.sensor !== SENSOR_TIME_OF_DAY) return null;
  if (node.compare.op !== op) return null;
  return parseOffsetOperand(node.compare.right, sensorId);
}

export function parseDayNightCondition(node: ConditionNode): DayNightCondition | null {
  if (node.all && node.all.length === 2) {
    let sunriseOffset: number | null = null;
    let sunsetOffset: number | null = null;
    for (const child of node.all) {
      const sunrise = parseSunCompare(child, 'gte', SENSOR_SUNRISE);
      if (sunrise !== null) {
        sunriseOffset = sunrise;
        continue;
      }
      const sunset = parseSunCompare(child, 'lt', SENSOR_SUNSET);
      if (sunset !== null) {
        sunsetOffset = sunset;
      }
    }
    if (sunriseOffset !== null && sunsetOffset !== null) {
      return {
        type: 'daytime',
        startOffsetSeconds: sunriseOffset,
        endOffsetSeconds: sunsetOffset,
      };
    }
  }

  if (node.any && node.any.length === 2) {
    let sunsetOffset: number | null = null;
    let sunriseOffset: number | null = null;
    for (const child of node.any) {
      const sunset = parseSunCompare(child, 'gte', SENSOR_SUNSET);
      if (sunset !== null) {
        sunsetOffset = sunset;
        continue;
      }
      const sunrise = parseSunCompare(child, 'lt', SENSOR_SUNRISE);
      if (sunrise !== null) {
        sunriseOffset = sunrise;
      }
    }
    if (sunsetOffset !== null && sunriseOffset !== null) {
      return {
        type: 'nighttime',
        startOffsetSeconds: sunsetOffset,
        endOffsetSeconds: sunriseOffset,
      };
    }
  }

  return null;
}

function buildOffsetOperand(sensorId: string, offsetSeconds: number): Operand {
  if (offsetSeconds === 0) {
    return { sensor: sensorId };
  }
  if (offsetSeconds > 0) {
    return { add: [{ sensor: sensorId }, { literal: offsetSeconds }] };
  }
  return { sub: [{ sensor: sensorId }, { literal: Math.abs(offsetSeconds) }] };
}

export function buildDayNightCondition(
  type: DayNightCondition['type'],
  startOffsetSeconds: number,
  endOffsetSeconds: number
): ConditionNode {
  const startSensor = type === 'daytime' ? SENSOR_SUNRISE : SENSOR_SUNSET;
  const endSensor = type === 'daytime' ? SENSOR_SUNSET : SENSOR_SUNRISE;
  const startCompare: CompareCondition = {
    left: { sensor: SENSOR_TIME_OF_DAY },
    op: 'gte',
    right: buildOffsetOperand(startSensor, startOffsetSeconds),
  };
  const endCompare: CompareCondition = {
    left: { sensor: SENSOR_TIME_OF_DAY },
    op: 'lt',
    right: buildOffsetOperand(endSensor, endOffsetSeconds),
  };
  if (type === 'daytime') {
    return { all: [{ compare: startCompare }, { compare: endCompare }] };
  }
  return { any: [{ compare: startCompare }, { compare: endCompare }] };
}

/**
 * Generate a human-readable summary of a condition tree
 * Uses multi-line format with indentation for clear grouping
 */
export function generateSummary(
  node: ConditionNode | null,
  sensors: SensorInfo[],
  indent: number = 0
): string {
  if (!node) return '';

  const findSensor = (id: string) => sensors.find((s) => s.humanId === id || s.id === id);

  const operandToString = (op: Operand): string => {
    if ('literal' in op) return String(op.literal);
    if ('sensor' in op) {
      const sensor = findSensor(op.sensor);
      return sensor?.name ?? op.sensor;
    }
    if ('add' in op) {
      const [base, offset] = op.add;
      const baseStr = operandToString(base);
      const offsetVal = (offset as { literal?: number }).literal ?? 0;
      return offsetVal >= 0 ? `${baseStr} + ${offsetVal}` : `${baseStr} - ${Math.abs(offsetVal)}`;
    }
    if ('sub' in op) {
      const [base, offset] = op.sub;
      const baseStr = operandToString(base);
      const offsetVal = (offset as { literal?: number }).literal ?? 0;
      return `${baseStr} - ${offsetVal}`;
    }
    return '?';
  };

  const opSymbols: Record<string, string> = {
    eq: '=',
    ne: '≠',
    gt: '>',
    gte: '≥',
    lt: '<',
    lte: '≤',
  };
  const offSymbols: Record<string, string> = {
    gt: '≤',
    gte: '<',
    lt: '≥',
    lte: '>',
  };

  // Format time with full seconds precision (HH:MM:SS)
  const formatTime = (time: string): string => {
    // Ensure we show full HH:MM:SS format
    if (time.length >= 8) return time.slice(0, 8);
    if (time.length >= 5) return time.slice(0, 5) + ':00';
    return time;
  };

  const formatOffsetMinutes = (offsetSeconds: number): string => {
    const minutes = offsetSeconds / 60;
    const absMinutes = Math.abs(minutes);
    const formatted = Number.isInteger(absMinutes)
      ? absMinutes.toString()
      : absMinutes.toFixed(1).replace(/\.0$/, '');
    const sign = minutes < 0 ? '-' : '+';
    return `${sign}${formatted}m`;
  };

  const nodeToString = (n: ConditionNode, depth: number): string => {
    const dayNight = parseDayNightCondition(n);
    if (dayNight) {
      const label = dayNight.type === 'daytime' ? 'Daytime' : 'Nighttime';
      const parts: string[] = [];
      if (dayNight.startOffsetSeconds !== 0) {
        parts.push(`start ${formatOffsetMinutes(dayNight.startOffsetSeconds)}`);
      }
      if (dayNight.endOffsetSeconds !== 0) {
        parts.push(`end ${formatOffsetMinutes(dayNight.endOffsetSeconds)}`);
      }
      if (parts.length === 0) {
        return label;
      }
      return `${label} (${parts.join(', ')})`;
    }

    const prefix = '  '.repeat(depth);

    if (n.all) {
      if (n.all.length === 0) return '(empty)';
      // For simple flat conditions at root, use inline format
      if (depth === 0 && n.all.every((c) => !c.all && !c.any)) {
        return n.all.map((c) => nodeToString(c, 0)).join(' AND ');
      }
      // Multi-line format with ALL label
      const children = n.all.map((c) => prefix + '  • ' + nodeToString(c, depth + 1)).join('\n');
      return depth === 0 ? `ALL:\n${children}` : `ALL:\n${children}`;
    }
    if (n.any) {
      if (n.any.length === 0) return '(empty)';
      // For simple flat conditions, use inline with parens
      if (n.any.every((c) => !c.all && !c.any)) {
        const inner = n.any.map((c) => nodeToString(c, 0)).join(' OR ');
        return n.any.length > 1 ? `(${inner})` : inner;
      }
      // Multi-line format with ANY label
      const children = n.any.map((c) => prefix + '  • ' + nodeToString(c, depth + 1)).join('\n');
      return `ANY:\n${children}`;
    }
    if (n.not) {
      return `NOT ${nodeToString(n.not, depth)}`;
    }
    if (n.compare) {
      const left = operandToString(n.compare.left);
      const op = opSymbols[n.compare.op] ?? n.compare.op;
      const right = operandToString(n.compare.right);
      let summary = `${left} ${op} ${right}`;
      if (n.compare.hysteresis) {
        const offOp = offSymbols[n.compare.op] ?? '?';
        summary += ` (off ${offOp} ${n.compare.hysteresis.release})`;
      }
      return summary;
    }
    if (n.timeBetween) {
      const start = formatTime(n.timeBetween.start);
      const end = formatTime(n.timeBetween.end);
      return `Time between ${start} and ${end}`;
    }
    return '?';
  };

  return nodeToString(node, indent);
}

/**
 * Validate a condition node and return an error message if invalid
 */
export function validateConditionNode(
  node: ConditionNode | null,
  options?: { allowHysteresis?: boolean }
): string | null {
  if (!node) return null;
  const allowHysteresis = options?.allowHysteresis ?? true;

  if (node.all) {
    for (const child of node.all) {
      const error = validateConditionNode(child, options);
      if (error) return error;
    }
  }

  if (node.any) {
    if (node.any.length === 0 && isDayOfWeekGroup(node)) {
      return 'Select at least one day of the week';
    }
    for (const child of node.any) {
      const error = validateConditionNode(child, options);
      if (error) return error;
    }
  }

  if (node.compare) {
    const left = node.compare.left as { sensor?: string };
    if (!left.sensor) return 'Comparison missing left sensor';
    if (!node.compare.op) return 'Comparison missing operator';

    if (node.compare.hysteresis) {
      if (!allowHysteresis) {
        return 'Separate off threshold is only supported for triggers';
      }

      if (node.compare.op === 'eq' || node.compare.op === 'ne') {
        return 'Separate off threshold requires gt, gte, lt, or lte comparisons';
      }

      const rightLiteral = (node.compare.right as { literal?: number }).literal;
      if (rightLiteral === undefined) {
        return 'Separate off threshold requires a literal value';
      }

      const release = node.compare.hysteresis.release;
      if (release === undefined || Number.isNaN(release)) {
        return 'Separate off threshold requires a release value';
      }

      if (node.compare.op === 'gt' || node.compare.op === 'gte') {
        if (release >= rightLiteral) {
          return 'Off threshold must be lower than the activation value';
        }
      }

      if (node.compare.op === 'lt' || node.compare.op === 'lte') {
        if (release <= rightLiteral) {
          return 'Off threshold must be higher than the activation value';
        }
      }
    }
  }

  if (node.timeBetween) {
    if (!node.timeBetween.start) return 'Time condition missing start time';
    if (!node.timeBetween.end) return 'Time condition missing end time';
  }

  return null;
}
